import * as signalR from "@microsoft/signalr";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  allDatasets,
  allTemplates,
  signalRIssuerId,
  userEntries,
} from "../components/Common/Store/CommonSignals";

const SignalRComponent = () => {
  const [connection, setConnection] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const MAX_RETRIES = 5;
  const [retryCount, setRetryCount] = useState(0);
  const envVariables = useSelector((state) => state.env.EnvVariables);

  // Initialize connection
  useEffect(() => {
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${envVariables.SIGNALR_NEGOTIATE}`)
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    newConnection.on("ReceiveMessage", (message) => {
      setNewMessage(message);
    });

    newConnection.start().catch((e) => console.log("Connection failed: ", e));

    setConnection(newConnection);

    return () => {
      newConnection.off("ReceiveMessage");
      newConnection.stop().catch((e) => console.log("Error stopping connection: ", e));
    };
  }, []);

  // ResourceType and ActionType mapping
  const signalRMessageHandlers = {
    Template: {
      CREATE: handleTemplateCreate,
      UPDATE: handleTemplateUpdate,
      DELETE: handleTemplateDelete,
    },
    Dataset: {
      CREATE: handleDatasetCreate,
      UPDATE: handleDatasetUpdate,
      DELETE: handleDatasetDelete,
    },
    Entry: {
      CREATE: handleEntryCreate,
      UPDATE: handleEntryUpdate,
      DELETE: handleEntryDelete,
    },
  };

  // Message handling
  useEffect(() => {
    if (newMessage) {
      const { IssuerId, ActionType, ResourceType, UpdatedEntity } = JSON.tryParse(newMessage, "{}");

      // Assign the correct handler to the resource and action types
      const resourceHandlers = signalRMessageHandlers[ResourceType];
      if (resourceHandlers) {
        const actionHandler = resourceHandlers[ActionType];
        if (actionHandler) {
          actionHandler(IssuerId, UpdatedEntity);
        } else {
          console.log(`No handler found for ActionType: ${ActionType}`);
        }
      } else {
        console.log(`No handler found for ResourceType: ${ResourceType}`);
      }
    }
  }, [newMessage]);

  function handleTemplateCreate(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      const updatedTemplates = [...allTemplates.value, entity];
      allTemplates.value = updatedTemplates;

      console.log("Template created:", entity.id);
    }
  }

  function handleTemplateUpdate(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      const updatedTemplates = allTemplates.value.map((template) =>
        template.id === entity.id ? { ...template, ...entity } : template
      );

      allTemplates.value = updatedTemplates;
      console.log("Template updated:", entity.id);
    }
  }

  function handleTemplateDelete(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      const updatedTemplates = allTemplates.value.filter((template) => template.id !== entity.id);

      allTemplates.value = updatedTemplates;

      console.log("Template deleted:", entity.id);
    }
  }

  function handleDatasetCreate(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      const updatedDatasets = [...allDatasets.value, entity];
      allDatasets.value = updatedDatasets;

      console.log("Dataset created:", entity.id);
    }
  }

  function handleDatasetUpdate(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      const updatedDatasets = allDatasets.value.map((dataset) =>
        dataset.id === entity.id ? entity : dataset
      );

      allDatasets.value = updatedDatasets;
      console.log("Dataset updated:", entity.id);
    }
  }

  function handleDatasetDelete(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      const updatedDatasets = allDatasets.value.filter((dataset) => dataset.id !== entity.id);

      allDatasets.value = updatedDatasets;

      console.log("Dataset deleted:", entity.id);
    }
  }

  function handleEntryCreate(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      if (userEntries.value?.[entity.templateId]) {
        const newEntries = { ...userEntries.value };
        newEntries[entity.templateId].unshift(entity);
        userEntries.value = newEntries;

        console.log("Entry created: ", entity.id);
      }
    }
  }

  function handleEntryUpdate(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      if (userEntries.value?.[entity.templateId]) {
        const newEntries = { ...userEntries.value };
        const entryIndex = newEntries[entity.templateId].findIndex(
          (entry) => entry.id === entity.id
        );
        newEntries[entity.templateId][entryIndex] = entity;
        userEntries.value = newEntries;

        console.log("Entry updated: ", entity.id);
      }
    }
  }

  function handleEntryDelete(IssuerId, entity) {
    if (IssuerId !== signalRIssuerId.value) {
      if (userEntries.value?.[entity.TemplateId]) {
        const tempEntries = { ...userEntries.value };
        tempEntries[entity.TemplateId] = tempEntries[entity.TemplateId].filter(
          (entry) => entry.id !== entity.id
        );
        userEntries.value = tempEntries;

        console.log("Entry deleted: ", entity.id);
      }
    }
  }

  // Connection retry logic
  useEffect(() => {
    if (!connection) return;

    connection.onclose((e) => {
      console.log("Connection closed: ", e);

      if (retryCount < MAX_RETRIES) {
        const delay = Math.min(5000 * Math.pow(2, retryCount), 30000);
        setTimeout(() => {
          connection
            .start()
            .then(() => {
              console.log("Reconnected successfully");
              setRetryCount(0);
            })
            .catch((err) => {
              console.log("Failed to reconnect", err);
              setRetryCount((prevCount) => prevCount + 1);
            });
        }, delay);
      }
    });

    return () => {
      connection.off("ReceiveMessage");
      connection.stop().catch((e) => console.log("Error stopping connection: ", e));
    };
  }, [connection, retryCount]);
};

export default SignalRComponent;
